import { z } from 'zod';
import { addDays } from './facilities-preventive.ts';
import { fromScaled, toScaled } from './money.ts';

/**
 * Orçamento do FP&A no caixa (ADR 0084, Entrega 6; D-05, D-06, D-20). Funções puras: recebem as
 * linhas da versão vigente do FP&A (contrato C-FPA-1), os títulos a pagar e as previsões por chave,
 * e devolvem o orçamento ainda não coberto, os indicadores por lançamento, os impactos de caixa
 * fora do orçamento e o comparativo Orçado × Forecast × Caixa. Sem a leitura do FP&A, nada é
 * estimado (nunca zero no lugar de "sem informação").
 */

export interface BudgetKey {
  costCenterId: string;
  expenseTypeId: string;
  classificationId: string;
}

export const BUDGET_NATURES = ['revenue', 'opex', 'investing', 'financing'] as const;
export type BudgetNature = (typeof BUDGET_NATURES)[number];

export interface BudgetLine extends BudgetKey {
  /** Mês de competência (dia 1). */
  month: string;
  /** Positivo = saída prevista no orçamento (receita também positiva, com natureza `revenue`). */
  amount: string;
  currency: string;
  nature: BudgetNature;
  isPayroll: boolean;
}

/** Título, previsão ou pagamento por chave (a chave pode estar incompleta). */
export interface KeyedAmount {
  costCenterId: string | null;
  expenseTypeId: string | null;
  classificationId: string | null;
  month: string;
  currency: string;
  amount: string;
}

export interface BudgetRule {
  expenseTypeId: string | null;
  classificationId: string | null;
  anchor: 'competence_end' | 'competence_start';
  lagDays: number;
  validFrom: string;
  validTo: string | null;
}

export interface BudgetAction extends BudgetKey {
  month: string;
  action: 'discard' | 'reschedule';
  newDate: string | null;
}

export interface BudgetEstimate extends BudgetKey {
  month: string;
  currency: string;
  nature: BudgetNature;
  isPayroll: boolean;
  budget: string;
  covered: string;
  uncovered: string;
  /** Data do caixa: regra de defasagem, último dia do mês (D-06) ou a reprogramada. */
  date: string;
  /** Agendada; chegou a data sem lançamento (aviso, até descartar ou reprogramar); reprogramada. */
  status: 'scheduled' | 'without_entry' | 'rescheduled';
}

export const keyOf = (k: {
  costCenterId: string | null;
  expenseTypeId: string | null;
  classificationId: string | null;
}) => `${k.costCenterId ?? '-'}|${k.expenseTypeId ?? '-'}|${k.classificationId ?? '-'}`;
const complete = (k: KeyedAmount): boolean =>
  !!k.costCenterId && !!k.expenseTypeId && !!k.classificationId;
const pad = (v: number) => String(v).padStart(2, '0');

export function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
}

/** Data do caixa da estimativa: regra do par, regra geral da empresa ou o fim do mês (D-06). */
export function estimateDate(month: string, key: BudgetKey, rules: readonly BudgetRule[]): string {
  const valid = rules.filter(
    (r) => r.validFrom <= lastDayOfMonth(month) && (r.validTo === null || r.validTo >= month),
  );
  const rule =
    valid.find(
      (r) => r.expenseTypeId === key.expenseTypeId && r.classificationId === key.classificationId,
    ) ?? valid.find((r) => r.expenseTypeId === null);
  if (!rule) return lastDayOfMonth(month);
  const base = rule.anchor === 'competence_start' ? month : lastDayOfMonth(month);
  return addDays(base, rule.lagDays);
}

function sumBy(items: readonly KeyedAmount[]): Map<string, bigint> {
  const out = new Map<string, bigint>();
  for (const i of items) {
    const k = `${keyOf(i)}|${i.month}|${i.currency}`;
    out.set(k, (out.get(k) ?? 0n) + toScaled(i.amount));
  }
  return out;
}

/**
 * Orçamento não coberto (camada 4): por chave e mês ≥ o atual, máx(0, orçamento − títulos −
 * previsões da mesma chave e competência). Receita fica fora (não é estimativa de caixa).
 */
export function budgetEstimates(input: {
  today: string;
  lines: readonly BudgetLine[];
  payables: readonly KeyedAmount[];
  forecasts: readonly KeyedAmount[];
  actions: readonly BudgetAction[];
  rules: readonly BudgetRule[];
}): BudgetEstimate[] {
  const currentMonth = `${input.today.slice(0, 7)}-01`;
  const covered = sumBy([...input.payables, ...input.forecasts]);
  const actionOf = new Map(input.actions.map((a) => [`${keyOf(a)}|${a.month}`, a]));
  const budget = new Map<string, BudgetLine & { total: bigint }>();
  for (const l of input.lines) {
    if (l.nature === 'revenue' || l.month < currentMonth) continue;
    const k = `${keyOf(l)}|${l.month}|${l.currency}`;
    const cur = budget.get(k);
    budget.set(k, { ...l, total: (cur?.total ?? 0n) + toScaled(l.amount) });
  }
  const out: BudgetEstimate[] = [];
  for (const [k, l] of budget) {
    const used = covered.get(k) ?? 0n;
    const left = l.total - used;
    if (left <= 0n) continue;
    const action = actionOf.get(`${keyOf(l)}|${l.month}`);
    if (action?.action === 'discard') continue;
    const date =
      action?.action === 'reschedule' ? action.newDate! : estimateDate(l.month, l, input.rules);
    out.push({
      costCenterId: l.costCenterId,
      expenseTypeId: l.expenseTypeId,
      classificationId: l.classificationId,
      month: l.month,
      currency: l.currency,
      nature: l.nature,
      isPayroll: l.isPayroll,
      budget: fromScaled(l.total),
      covered: fromScaled(used),
      uncovered: fromScaled(left),
      date,
      status: action ? 'rescheduled' : date < input.today ? 'without_entry' : 'scheduled',
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

/** Indicador do lançamento frente ao orçamento (§8 da especificação). */
export type BudgetIndicator = 'within' | 'outside' | 'unclassified' | 'possible_duplicate';

export interface BudgetedEntry extends KeyedAmount {
  type: 'payable' | 'forecast';
  id: string;
  description: string;
  date: string;
  origin: string;
  responsible: string | null;
}

/**
 * Indicadores por lançamento: sem chave completa = sem classificação; sem orçamento na chave e no
 * mês, ou passando dele = fora; previsão com título igual (chave, mês e valor) = possível
 * duplicidade; o resto, dentro.
 */
export function budgetIndicators(
  entries: readonly BudgetedEntry[],
  lines: readonly BudgetLine[],
): Map<string, BudgetIndicator> {
  const budget = new Map<string, bigint>();
  for (const l of lines) {
    const k = `${keyOf(l)}|${l.month}|${l.currency}`;
    budget.set(k, (budget.get(k) ?? 0n) + toScaled(l.amount));
  }
  const used = new Map<string, bigint>();
  const out = new Map<string, BudgetIndicator>();
  const payableSig = new Set(
    entries
      .filter((e) => e.type === 'payable')
      .map((e) => `${keyOf(e)}|${e.month}|${e.currency}|${toScaled(e.amount)}`),
  );
  for (const e of [...entries].sort((a, b) => a.date.localeCompare(b.date))) {
    const id = `${e.type}:${e.id}`;
    if (!complete(e)) {
      out.set(id, 'unclassified');
      continue;
    }
    const sig = `${keyOf(e)}|${e.month}|${e.currency}`;
    if (e.type === 'forecast' && payableSig.has(`${sig}|${toScaled(e.amount)}`)) {
      out.set(id, 'possible_duplicate');
      continue;
    }
    const total = (used.get(sig) ?? 0n) + toScaled(e.amount);
    used.set(sig, total);
    const limit = budget.get(sig);
    out.set(id, limit === undefined || total > limit ? 'outside' : 'within');
  }
  return out;
}

/** "Impactos de Caixa Não Previstos no Orçamento": fora do orçamento ou sem classificação. */
export function unbudgetedImpacts(
  entries: readonly BudgetedEntry[],
  lines: readonly BudgetLine[],
): (BudgetedEntry & { indicator: BudgetIndicator })[] {
  const ind = budgetIndicators(entries, lines);
  return entries
    .map((e) => ({ ...e, indicator: ind.get(`${e.type}:${e.id}`)! }))
    .filter((e) => e.indicator === 'outside' || e.indicator === 'unclassified')
    .sort((a, b) => a.date.localeCompare(b.date));
}

export interface ComparisonRow {
  month: string;
  nature: BudgetNature | 'unclassified';
  currency: string;
  budget: string;
  forecast: string;
  committed: string;
  projected: string;
  realized: string;
}

/**
 * Orçado × Forecast × Caixa previsto × Caixa realizado, por mês, natureza e moeda. O comprometido
 * (pedido emitido e não faturado, C-CMP-1) só aparece aqui. A natureza vem da linha do orçamento
 * da chave; sem linha, "sem classificação".
 */
export function budgetComparison(input: {
  budget: readonly BudgetLine[];
  forecast: readonly BudgetLine[];
  committed: readonly KeyedAmount[];
  projected: readonly KeyedAmount[];
  realized: readonly KeyedAmount[];
}): ComparisonRow[] {
  const natureOf = new Map<string, BudgetNature>();
  for (const l of [...input.budget, ...input.forecast]) natureOf.set(keyOf(l), l.nature);
  const rows = new Map<
    string,
    Record<'budget' | 'forecast' | 'committed' | 'projected' | 'realized', bigint>
  >();
  const add = (
    field: 'budget' | 'forecast' | 'committed' | 'projected' | 'realized',
    items: readonly KeyedAmount[],
  ) => {
    for (const i of items) {
      const nature = natureOf.get(keyOf(i)) ?? 'unclassified';
      const k = `${i.month}|${nature}|${i.currency}`;
      const cur = rows.get(k) ?? {
        budget: 0n,
        forecast: 0n,
        committed: 0n,
        projected: 0n,
        realized: 0n,
      };
      cur[field] += toScaled(i.amount);
      rows.set(k, cur);
    }
  };
  add('budget', input.budget);
  add('forecast', input.forecast);
  add('committed', input.committed);
  add('projected', input.projected);
  add('realized', input.realized);
  return [...rows.entries()]
    .map(([k, v]) => {
      const [month, nature, currency] = k.split('|') as [string, ComparisonRow['nature'], string];
      return {
        month,
        nature,
        currency,
        budget: fromScaled(v.budget),
        forecast: fromScaled(v.forecast),
        committed: fromScaled(v.committed),
        projected: fromScaled(v.projected),
        realized: fromScaled(v.realized),
      };
    })
    .sort((a, b) => a.month.localeCompare(b.month) || a.nature.localeCompare(b.nature));
}

// ---------------------------------------------------------------------------
// Entradas da API
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida');
const month = z.string().regex(/^\d{4}-\d{2}-01$/, 'Mês inválido');

export const budgetEstimateActionInput = z
  .object({
    legalEntityId: z.uuid(),
    costCenterId: z.uuid(),
    expenseTypeId: z.uuid(),
    classificationId: z.uuid(),
    month,
    action: z.enum(['discard', 'reschedule']),
    newDate: isoDate.nullable().optional(),
    reason: z.string().trim().min(3).max(1000),
  })
  .refine((a) => (a.action === 'reschedule') === !!a.newDate, {
    message: 'Reprogramar pede a nova data',
    path: ['newDate'],
  });

export const budgetCashRuleInput = z
  .object({
    legalEntityId: z.uuid(),
    expenseTypeId: z.uuid().nullable().optional(),
    classificationId: z.uuid().nullable().optional(),
    anchor: z.enum(['competence_end', 'competence_start']),
    lagDays: z.number().int().min(-90).max(365),
    validFrom: isoDate,
    validTo: isoDate.nullable().optional(),
  })
  .refine((r) => !r.expenseTypeId === !r.classificationId, {
    message: 'Informe o Tipo de Gasto e o Tipo de Classificação juntos',
    path: ['classificationId'],
  });
